import { gql } from '@apollo/client';

const ENDPOINT = {
    GET_LOGIN_OPERATION: gql`
        query login(
            $email: String!
            $password: String!
            $recaptchaVersion: RecaptchaVersion!
            $recaptchaToken: String!
            $brand: BillingSystem
            $deviceId: String
        ) {
            login(
                email: $email
                password: $password
                recaptchaVersion: $recaptchaVersion
                recaptchaToken: $recaptchaToken
                brand: $brand
                deviceId: $deviceId
            ) {
                accessToken
                accessTokenExpiresIn
                refreshToken
                refreshTokenExpiresIn
                statusCode
                passwordResetToken
                numberOfAuthenticationAttemptsRemaining
                isION
                isR6
                mfaForced
                offlineToken
                mfaToken
                otpDelivery
                biometricOptedIn
                otpUuid
                contactFound
                services {
                    number
                    type
                }
            }
        }
    `,
    RESET_PASSWORD: gql`
        mutation resetPassword($emailAddress: String!, $brand: BillingSystem) {
            resetPassword(emailAddress: $emailAddress, brand: $brand) {
                message
            }
        }
    `,

    GET_ACCOUNT_TYPE: gql`
        query accountType {
            accountType {
                postpay
                prepay
            }
        }
    `,
    GET_FEATURES: gql`
        query getFeature($username: String!) {
            getFeature(username: $username) {
                features {
                    id
                    feature_name
                    feature_description
                }
            }
        }
    `,
    VALIDATE_MFA_OTP: gql`
        mutation validateMFAOTP(
            $uuid: String!
            $otp: String!
            $operation: MFATokenOperation!
            $contactNumber: String
        ) {
            validateMFAOTP(
                uuid: $uuid
                otp: $otp
                operation: $operation
                contactNumber: $contactNumber
            ) {
                mfaToken
                deviceId
                contactUpdateStatus
            }
        }
    `,
    SEND_MFA_OTP: gql`
        mutation sendMFAOTP(
            $username: String
            $operation: MFATokenOperation!
            $contactNumber: String
            $contactUUID: String
        ) {
            sendMFAOTP(
                username: $username
                operation: $operation
                contactNumber: $contactNumber
                contactUUID: $contactUUID
            ) {
                uuid
                deliveryMethod
                mfaForced
            }
        }
    `,
    BIOMETRICS_PREFERENCE: gql`
        mutation biometricsPreference($deviceId: String!, $biometricsPreference: Boolean!) {
            biometricsPreference(deviceId: $deviceId, biometricsPreference: $biometricsPreference) {
                status
            }
        }
    `,
    LATEST_APP_VERSION: gql`
        query latestAppVersion {
            latestAppVersion {
                latestVersion
                recentVersions {
                    version
                    createdDate
                    update
                }
            }
        }
    `,
};

/**
 * @name getLoginOperation - To authenticate users
 * @param {Object} Obj - attributes for the function
 * @param {GraphQL} Obj.graphql - appoloclient graphql constant
 * @param {GraphQL} graphql - appoloclient graphql constant
 * @returns
 */
export const getLoginOperation = async ({
    graphql,
    email,
    password,
    recaptcha,
    recaptchaVersion,
    deviceId,
}) => {
    try {
        const data = await graphql.query({
            query: ENDPOINT.GET_LOGIN_OPERATION,
            variables: {
                email,
                password,
                recaptchaToken: recaptcha,
                recaptchaVersion,
                deviceId,
            },
        });

        return data;
    } catch (error) {
        throw new Error(error);
    }
};

/**
 * @name resetPassword - To reset users password
 * @param {Object} Obj - attributes for the function
 * @param {GraphQL} Obj.graphql - appoloclient graphql constant
 * @param {GraphQL} graphql - appoloclient graphql constant
 * @returns
 */
export const resetPassword = async ({
    graphql,
    emailAddress,
    brand,
    recaptcha,
    recaptchaVersion,
}) => {
    try {
        const variables = {
            emailAddress,
        };
        if (brand) {
            variables.brand = brand;
        }
        let context;
        if (recaptcha && recaptchaVersion) {
            context = {
                headers: {
                    recaptchaToken: recaptcha,
                    recaptchaVersion,
                },
            };
        }

        const res = await graphql.mutate({
            mutation: ENDPOINT.RESET_PASSWORD,
            context: context ? context : '',
            variables,
        });

        return res;
    } catch (error) {
        throw new Error(error);
    }
};

export const getAccountType = async ({ graphql }) => {
    const { data } = await graphql.query({
        query: ENDPOINT.GET_ACCOUNT_TYPE,
    });
    return data;
};
export const getFeature = async ({ graphql, username }) => {
    const { data } = await graphql.query({
        query: ENDPOINT.GET_FEATURES,
        variables: {
            username,
        },
    });
    return data?.getFeature;
};

export const validateMfaOtp = async ({ graphql, otp, otpUuid, operation, contactNumber }) => {
    const data = await graphql.mutate({
        mutation: ENDPOINT.VALIDATE_MFA_OTP,
        variables: {
            otp,
            uuid: otpUuid,
            operation,
            contactNumber,
        },
    });
    return data;
};

export const sendMfaOtp = async ({ graphql, username, operation, contactNumber, contactUUID }) => {
    const { data } = await graphql.mutate({
        mutation: ENDPOINT.SEND_MFA_OTP,
        variables: {
            username,
            operation,
            contactNumber,
            contactUUID,
        },
    });
    return data;
};

export const biometricsPreferenceApi = async ({ graphql, deviceId, biometricsPreference }) => {
    const { data } = await graphql.mutate({
        mutation: ENDPOINT.BIOMETRICS_PREFERENCE,
        variables: {
            deviceId,
            biometricsPreference,
        },
    });
    return data;
};

export const getLatestAppVersionApi = async ({ graphql }) => {
    const { data } = await graphql.query({
        query: ENDPOINT.LATEST_APP_VERSION,
    });
    return data;
};
